import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

interface ModalContextValue {
  /** True if any modal is currently open */
  isAnyModalOpen: boolean;
  /** Call when opening a modal */
  registerModal: () => void;
  /** Call when closing a modal */
  unregisterModal: () => void;
}

const ModalContext = createContext<ModalContextValue | null>(null);

interface ModalProviderProps {
  children: ReactNode;
}

export function ModalProvider({ children }: ModalProviderProps) {
  const [openModalCount, setOpenModalCount] = useState(0);

  const registerModal = useCallback(() => {
    setOpenModalCount(count => count + 1);
  }, []);

  const unregisterModal = useCallback(() => {
    setOpenModalCount(count => Math.max(0, count - 1));
  }, []);

  return (
    <ModalContext.Provider
      value={{
        isAnyModalOpen: openModalCount > 0,
        registerModal,
        unregisterModal,
      }}
    >
      {children}
    </ModalContext.Provider>
  );
}

export function useModalContext(): ModalContextValue {
  const context = useContext(ModalContext);
  if (!context) {
    // Return a default value if used outside provider (for backwards compatibility)
    return {
      isAnyModalOpen: false,
      registerModal: () => {},
      unregisterModal: () => {},
    };
  }
  return context;
}

export default ModalContext;
